import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Curso } from 'src/app/models/curso';
import { Eixo } from 'src/app/models/eixo';

@Injectable({ providedIn: 'root' })
export class APIService {

  baseUrl = environment.baseApiUrl;

  constructor(private http: HttpClient) { }

  getCurso(coursoId: number) {
    return this.http.get<{}>(`${this.baseUrl}/cursos/${coursoId}`).pipe(
      mergeMap((curso: Curso) => {
        const eixoId = curso.eixoTecnologicoId;
        return this.http.get(`${this.baseUrl}/eixos/${eixoId}`).pipe(
          map((eixo: Eixo) => {
            return {
              ...curso,
              eixo: { nome: eixo.nome, id: eixo.id },
            };
          })
        );
      }),
    );
  }

  getEixos(): any {
    return this.http.get<[]>(`${this.baseUrl}/eixos`);
  }

  getEixo(id: number) {
    return this.http.get<{}>(`${this.baseUrl}/eixos/${id}`);
  }

  getFaq(query?: number, numeroPagina?: number): any {
    const str = query ? query.toString() : '';
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';

    let paramsBusca = new HttpParams().set('q', str);
    paramsBusca = paramsBusca.append('pagina', numeroPg);

    return this.http.get<[]>(`${this.baseUrl}/faq/`, {
      params: paramsBusca,
    });
  }

  getCursos(
    eixoId?: number,
    areaId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const AREAID = areaId && areaId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    if (AREAID) {
      queryParams = queryParams.append('areatecnologica', AREAID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/cursos/busca/`, {
      params: queryParams,
    });
  }

  getBusca(query?: string, numeroPagina?: number): any {
    const str = query ? query : '';
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';

    let parametrosBusca = new HttpParams().set('q', str);
    parametrosBusca = parametrosBusca.append('pagina', numeroPg);
    return this.http.get<[]>(`${this.baseUrl}/pesquisa/busca`, {
      params: parametrosBusca,
    });
  }

  getPrincipalInfo(): any {
    return this.http.get<[]>(`${this.baseUrl}/home/`);
  }

  getCatalogoInfo(): any {
    return this.http.get<[]>(`${this.baseUrl}/catalogo/`);
  }

  postForm(form) {
    return this.http.post(`${this.baseUrl}/consultapublica`, form);
  }

  postConsultaPublica(form) {
    return this.http.post(`${this.baseUrl}/form/sugestao-consulta-publica`, form);
  }

  postInclusaoExperimental(form) {
    return this.http.post(`${this.baseUrl}/form/sugestao-curso-experimental`, form);
  }

  getCursosNaoPaginado(eixoId: number) {
    return this.http.get(`${this.baseUrl}/eixos/${eixoId}/cursos`);
  }

  getFormAreasTecnologicas(eixoId: number): any {
    return this.http.get(`${this.baseUrl}/form/areas-tecnologicas/poreixo/${eixoId}`);
  }

  getAreasTecnologicas(eixoId: number): any {
    return this.http.get(`${this.baseUrl}/eixos/${eixoId}/areas-tecnologicas`);
  }

  getTermos() {
    return this.http.get('assets/documents/lista_de_termos.txt', { responseType: 'text' });
  }

  // -------------------- CNCT -------------------------
  getEixosCNCT() {
    return this.http.get(`https://api.catalogosept.com.br/cnct/eixos`);
  }

  getCursosEixoCNCT(eixoId: number) {
    return this.http.get(`https://api.catalogosept.com.br/cnct/cursos/buscaporeixo/${eixoId}`);
  }

  postFormCNCT(form) {
    return this.http.post('https://api.catalogosept.com.br/cnct/consultapublica2', form);
  }

  getDadosConsultaCargasHorarias() {
    return this.http.get('https://api.catalogosept.com.br/cnct/cursos/arvore-carga-horaria');
  }

  // -------------- Catálogos ----------------------

  getEixosConsultaAreas(): any {
    return this.http.get('https://api.catalogosept.com.br/catalogos/form/eixos');
  }

  getAreasConsultaAreas(eixoId): any {
    return this.http.get(`https://api.catalogosept.com.br/catalogos/form/eixos/${eixoId}/areas-tecnologicas`);
  }

  getCursosConsultaAreas() {
    return this.http.get('https://api.catalogosept.com.br/catalogos/form/arvore-cursos-unificados');
  }

  postFormConsultaAreas(form) {
    return this.http.post('https://api.catalogosept.com.br/catalogos/form/sugestao-consulta-publica', form);
  }

  /**
   *
   * @param eixoId id do eixo.
   * @param numeroPagina número da página.
   * @param query parâmetros de consulta.
   * @param filtro filtro de consulta.
   * @param ordenacao tipo de ordenação.
   * @returns os cursos que mudaram de nome
   */
  getCursosTabelaConvergencia(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/cursos/convergentes/`, {
      params: queryParams,
    });
  }

  /**
   *
   * @param eixoId id do eixo.
   * @param numeroPagina número da página.
   * @param query parâmetros de consulta.
   * @param filtro filtro de consulta.
   * @param ordenacao tipo de ordenação.
   * @returns os cursos não incluídos ou excluídos - tabela submissão
   */
  getCursosTabelaSubmissao(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/cursos/submissao/`, {
      params: queryParams,
    });
  }

  /**
   *
   * @param eixoId id do eixo.
   * @param numeroPagina número da página.
   * @param query parâmetros de consulta.
   * @param filtro filtro de consulta.
   * @param ordenacao tipo de ordenação.
   * @returns retorna a area tecnológica.
   */
  getAreasTecnologicasPg(
    eixoId?: number,
    numeroPagina?: number,
    query?: string,
    filtro?: string,
    ordenacao?: string,
  ) {
    const EIXOID = eixoId && eixoId.toString();
    const numeroPg = numeroPagina ? numeroPagina.toString() : '1';
    let queryParams = new HttpParams();

    if (EIXOID) {
      queryParams = queryParams.append('eixo', EIXOID);
    }

    queryParams = queryParams.append('q', query ? query : '');
    queryParams = queryParams.append('filtro', filtro ? filtro : '');
    queryParams = queryParams.append('ordenacao', ordenacao ? ordenacao : '');
    queryParams = queryParams.append('pagina', numeroPg);

    return this.http.get<{}>(`${this.baseUrl}/eixos/${eixoId}/areas-tecnologicas/`, {
      params: queryParams,
    });
  }

  /** Busca versões do sistema */
  getVersion() {
    return this.http.get(`${this.baseUrl}/version/`, { responseType: 'text' });
  }

  /** Busca pdf cursos convergentes */
  getDownloadPdfCursosConvergentes() {
    return this.http.get(`${this.baseUrl}/pdf-cursos-convergentes/`, { responseType: 'blob' });
  }

  /** Busca pdf cursos submissao */
  getDownloadPdfCursosSubmissao() {
    return this.http.get(`${this.baseUrl}/pdf-cursos-submissao/`, { responseType: 'blob' });
  }

  /** Busca csv cursos convergentes */
  getDownloadCsvCursosConvergentes() {
    return this.http.get(`${this.baseUrl}/csv-cursos-convergentes/`, { responseType: 'blob' });
  }

  /** Busca csv cursos submissao */
  getDownloadCsvCursosSubmissao() {
    return this.http.get(`${this.baseUrl}/csv-cursos-submissao/`, { responseType: 'blob' });
  }

  /**
   *
   * @param encoding encoda o texto
   * @returns Catálogo em csv
   */
  getDownloadCsvCatalogoCncst(encoding?: string) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('encoding', encoding ? encoding : '');

    return this.http.get(`${this.baseUrl}/csv-catalogo/`, {
      params: queryParams,
      responseType: 'blob'
    });
  }

  getLegislacaoInfo() {
    return this.http.get<any[]>(`${this.baseUrl}/legislacoes/cncst`);
  }

  getAreaTecnologicaById(id: number) {
    return this.http.get<{}>(`${this.baseUrl}/areas-tecnologicas/${id}/`);
  }

  /**
   * Retorna lista de cursos convergentes.
   *
   */
  getCursosConvergentes() {
    return this.http.get<{}>(`${this.baseUrl}/cursos/listaCursosConvergentes/`);
  }
}
